import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { useTranslation } from "react-i18next";
import { selectedPayment } from "../../redux/slices/payments";
import { itemCount } from "../../redux/slices/counter";

import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { PaymentModel } from "../../models/payments/payment-model";
import AuthService from "../../services/autentication/auth.service";
import OrderService from "../../services/order/order.service";
import Helpers from "../../utils/helpers";
import orderServices from "../../services/orders/orderservice";
import { selectedGetway } from "../../redux/slices/getway-link";
import RouteConstant from "../../constants/route.constants";
import NewOrderReview from "../New-Invoice-Report/new-order-review";
import OrderPrint from "../New-Invoice-Report/new-order-print";
import AppLoading from "../app-loading/app-loading";

const PaymentConfirmDialog = lazy(() => import("../orders/payment-details-dialog"));
const UserSwipeMachineDialog = lazy(() => import("../SwipeMachine-UserAccess/userSwipeMachineDialog"));

interface SidecartPaymentProps {
    onBack: () => void;
    onNext: () => void;
    onChangeAddress: () => void;
    onClose: () => void;
    onOrderComplete?: () => void;
}

const SidecartPayment: React.FC<SidecartPaymentProps> = ({ onBack, onNext, onChangeAddress, onClose, onOrderComplete }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const [paymentModes, setPaymentModes] = useState<PaymentModel[]>([]);
    const [selectedId, setSelectedId] = useState<number>(0);

    const [orderId, setOrderId] = useState<number>(0);
    const [plutusRefId, setPlutusRefId] = useState<number>(0);
    const [isConfirmDialog, setIsConfirmDialog] = useState(false);
    const [showMachineDialog, setShowMachineDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [finalOrderData, setFinalOrderData] = useState<any>(null);
    
    // Success Dialog State
    const [showSuccess, setShowSuccess] = useState(false);
    const [successDetails, setSuccessDetails] = useState({
        amount: 0,
        date: "",
        deliveryDate: ""
    });
    const [visibleOrderDetail, setVisibleOrderDetail] = useState(false);
    const [detailsOrder, setDetailsOrder] = useState<any>(null);

    const selectedAddress = useAppSelector((state) => state.userAddress.selecedUserAddresses);
    const isAdmin = useAppSelector((state) => state.isAdmin);
    const deliveryMethod = useAppSelector((state) => state.deliveryMethod.selectedDeliveryMethod);
    const comment = useAppSelector(state => state.comment);
    const courierCharges = Number(JSON.parse(localStorage.getItem("courier-charges") || "0"));

    const [isCardAndUpiAllowed, setIsCardAndUpiAllowed] = useState<boolean>(false);

    useEffect(() => {
        const checkEligibility = async () => {
            try {
                const res = await OrderService.checkCardAndUpiEligibility();
                setIsCardAndUpiAllowed(res.isSuccess);
            } catch (error) {
                console.error("Error checking eligibility:", error);
                setIsCardAndUpiAllowed(false);
            }
        };
        checkEligibility();
    }, []);

    useEffect(() => {
        const buildModes = () => {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const isExecutive = user.roleID === 4 || user.roleID === 8;
            const isCourier = courierCharges > 0;

            let modes: PaymentModel[] = [];

            if (selectedAddress?.homeDeliveryAvailable && (!isAdmin && !isExecutive)) {
                if (!isCourier) {
                    modes = [
                        { id: 1, mode: t("payment_mode1"), code: "COD", modeImage: "/assests/images/cod.jpg", isSelected: true },
                        { id: 7, mode: "Online Payment", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: false }
                    ];
                } else {
                    modes = [{ id: 7, mode: "Online Payment", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: true }];
                }
            } else if (isAdmin || isExecutive) {
                if (!isCourier) {
                    modes = [
                        { id: 1, mode: t("payment_mode1"), code: "COD", modeImage: "/assests/images/cod.jpg", isSelected: true },
                        { id: 6, mode: "Payment Link", code: "PL", modeImage: "/assests/images/Betaallink.png", isSelected: false },
                        { id: 7, mode: "Online Payment", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: false }
                    ];
                    if (isCardAndUpiAllowed) {
                        modes.push({ id: 5, mode: "UPI", code: "OPTUPI", modeImage: "/assests/images/oup.jpg", isSelected: false });
                        modes.push({ id: 9, mode: "Card", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: false });
                    }
                } else {
                     modes = [
                        { id: 7, mode: "Online Payment", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: true },
                        { id: 6, mode: "Payment Link", code: "PL", modeImage: "/assests/images/Betaallink.png", isSelected: false }
                    ];
                    if (isCardAndUpiAllowed) {
                        modes.push({ id: 5, mode: "UPI", code: "OPTUPI", modeImage: "/assests/images/oup.jpg", isSelected: false });
                        modes.push({ id: 9, mode: "Card", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: false });
                    }
                }
            } else {
                // Regular user, no home delivery
                modes = [{ id: 7, mode: "Online Payment", code: "OPTONL", modeImage: "/assests/images/credit-card.png", isSelected: true }];
            }
            setPaymentModes(modes);
        };
        buildModes();
    }, [selectedAddress, isAdmin, courierCharges, isCardAndUpiAllowed, t]);

    const handleSelect = (mode: PaymentModel) => {
        setSelectedId(mode.id);
        dispatch(selectedPayment(mode));
        
        // Update local storage so if user goes back to summary it shows the selection
        const processedData = ProductStorageHelpers.GetProcessedProductStorage();
        if (processedData) {
            processedData.paymentOption = {
                id: mode.id.toString(),
                mode: mode.mode,
                code: mode.code,
                status: "Pending",
                description: mode.mode,
                dateTime: Date.now().toString()
            };
            ProductStorageHelpers.SetProcessedProductStorage(processedData);
        }
    };

    const handleViewOrderDetail = async (orderId: number) => {
        try {
            setIsLoading(true);
            const details = await orderServices.GetOrderByOrderId(orderId);
            if (details) {
                setDetailsOrder(details);
                setVisibleOrderDetail(true);
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No details found for this order' });
            }
        } catch (error: any) {
            console.error("Error while fetching order details:", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to fetch order details' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleContinue = async () => {
        const mode = paymentModes.find(m => m.id === selectedId);
        if (!mode) return;

        const processedData = ProductStorageHelpers.GetProcessedProductStorage();
        if (!processedData) return;

        // Update payment option
        processedData.paymentOption = {
            id: mode.id.toString(),
            mode: mode.mode,
            code: mode.code,
            status: "Pending",
            description: mode.mode,
            dateTime: Date.now().toString()
        };
        ProductStorageHelpers.SetProcessedProductStorage(processedData);
        const rewardDiscount = processedData.rewardDiscountAmount || 0;
        const computedGrandTotal = Math.max(0, Math.round(
            (processedData.totalItemCost ?? 0) + (processedData.totalTax ?? 0) + courierCharges - rewardDiscount
        ));
        setFinalOrderData({
            ...processedData,
            address: selectedAddress,
            courierCharges: courierCharges,
            grandTotal: computedGrandTotal,
            grandItemTotal: computedGrandTotal
        });

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const isExecutive = user.roleID === 4 || user.roleID === 8;

        setIsLoading(true);
        try {
            const finalData = {
                ...processedData,
                address: {
                    ...(selectedAddress || {}),
                    userAddressID: selectedAddress?.userAddressID || (selectedAddress as any)?.addressId || 0
                },
                userAddressID: selectedAddress?.userAddressID || (selectedAddress as any)?.addressId || 0,
                courierProviderID: deliveryMethod?.id === 0 ? 0 : (deliveryMethod?.id || 0),
                comment: comment,
                rewardPointsUsed: processedData.rewardPointsUsed || 0,
                rewardToCurrencyRatio: processedData.rewardToCurrencyRatio || 0,
                rewardDiscountAmount: processedData.rewardDiscountAmount || 0,
                grandTotal: computedGrandTotal
            };

            // Fast Pay for Swipe Machines (UPI/Card) for Admin/Executive
            if ((isAdmin || isExecutive) && (mode.mode === "UPI" || mode.mode === "Card")) {
                const eligibility = await OrderService.checkCardAndUpiEligibility();
                if (!eligibility.isSuccess) {
                    setShowMachineDialog(true);
                    setIsLoading(false);
                    return;
                }

                const response = isAdmin ?
                    await OrderService.setOrderForCustomer(finalData, Number(finalData.userId) || 0) :
                    await OrderService.setOrderFromCustomer(finalData);

                if (response.isSuccess) {
                    setOrderId(response.id);
                    setPlutusRefId(response.plutusTransactionReferenceID);
                    ProductStorageHelpers.SetProductStorage([]);
                    ProductStorageHelpers.ClearAllLocalStorage();
                    window.dispatchEvent(new Event('cartUpdated'));
                    dispatch(itemCount(0));
                    dispatch(selectedPayment(null));
                    setIsConfirmDialog(true);
                } else if (response.message?.toLowerCase().includes("swipe machine")) {
                    setShowMachineDialog(true);
                } else {
                    const errorDetail = response?.response?.data?.message || response?.message || 'Order placement failed';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: errorDetail });
                }
            } else {
                // Regular placement
                const res = isAdmin ? 
                    await OrderService.setOrderForCustomer(finalData, Number(finalData.userId) || 0) : 
                    await OrderService.setOrderFromCustomer(finalData);

                if (res.isSuccess) {
                    ProductStorageHelpers.SetProductStorage([]);
                    ProductStorageHelpers.ClearAllLocalStorage();
                    window.dispatchEvent(new Event('cartUpdated'));
                    dispatch(itemCount(0));
                    dispatch(selectedPayment(null));
                    setOrderId(res.id);
                    setSuccessDetails({
                        amount: computedGrandTotal,
                        date: Helpers.formatDateWithShortMonthSpaced(new Date()),
                        deliveryDate: res.additionalInfo?.expectedDeliveryDate || 'TBD'
                    });
                    
                    if (["Cash On Delivery", "Payment Link", "Card", "UPI"].includes(mode.mode)) {
                        setShowSuccess(true);
                    } else {
                        dispatch(selectedGetway({ paymentURL: res.additionalInfo?.paymentURL, expectedDeliveryDate: res.additionalInfo?.expectedDeliveryDate }));
                        onClose();
                        navigate(RouteConstant.getwayProcess);
                    }
                } else {
                    const errorDetail = res?.response?.data?.message || res?.message || 'Order placement failed';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: errorDetail });
                }
            }
        } catch (error: any) {
            console.error("Order error:", error);
            const errorDetail = error?.response?.data?.message || error?.message || 'Failed to place order';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: errorDetail });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-column h-full">
            <div className="side-cart-header">
                <h5><i className="pi pi-credit-card text-xl" /> Payment Method</h5>
                <div className="close-icon" onClick={onClose}>
                    <i className="pi pi-arrow-left" />
                </div>
            </div>

            <div className="side-cart-content text-sm">
                <div className="animate-in">
                    {/* Summary Recap Card */}
                    <div className="address-card selected bg-white mb-4 shadow-sm border-round-xl p-3 border-1 surface-border">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="grey-label m-0">Shipment Details</span>
                            <Button label="Edit" link className="p-0 text-xs text-primary font-bold" onClick={onChangeAddress} />
                        </div>
                        <div className="flex align-items-start gap-2 mb-3">
                            <i className="pi pi-map-marker mt-1 text-primary" />
                            <div>
                                <div className="font-bold text-gray-900">{selectedAddress?.userName}</div>
                                <div className="text-gray-600">
                                    {selectedAddress?.address1}
                                    <div className="text-xs">
                                        {selectedAddress?.areaName ? `${selectedAddress.areaName}, ` : ''}
                                        {selectedAddress?.cityName || (selectedAddress as any)?.city || ''},
                                        {selectedAddress?.stateName || (selectedAddress as any)?.state || ''} -
                                        {selectedAddress?.zipcode || (selectedAddress as any)?.pincode || ''}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex align-items-center gap-2 pt-2 border-top-1 surface-border">
                            <i className="pi pi-truck text-primary" />
                            <div className="text-gray-700 font-medium">
                                {deliveryMethod?.name} <span className="text-gray-400 mx-1">|</span> {deliveryMethod?.estimatedDays}
                            </div>
                        </div>
                    </div>

                    <span className="grey-label mb-3">Select Payment Method</span>
                    <div className="space-y-3">
                        {paymentModes.map(mode => (
                            <div
                                key={mode.id}
                                className={`address-card flex align-items-center gap-3 p-4 transition-all transition-duration-200 ${selectedId === mode.id ? 'selected scale-102 border-primary shadow-2' : 'hover:surface-50'}`}
                                onClick={() => handleSelect(mode)}
                            >
                                <RadioButton inputId={`pay-${mode.id}`} checked={selectedId === mode.id} value={mode.id} onChange={() => handleSelect(mode)} />
                                <div className="flex flex-column">
                                    <label htmlFor={`pay-${mode.id}`} className="font-bold text-gray-900 cursor-pointer">{mode.mode}</label>
                                    <span className="text-xs text-gray-500">Secure transaction</span>
                                </div>
                                {mode.modeImage && (
                                    <div className="ml-auto flex align-items-center gap-3">
                                        <img src={mode.modeImage} alt={mode.mode} style={{ height: '24px', width: 'auto', objectFit: 'contain' }} />
                                        {selectedId === mode.id && <i className="pi pi-check-circle text-xl text-primary" />}
                                    </div>
                                )}
                                {!mode.modeImage && selectedId === mode.id && <i className="pi pi-check-circle ml-auto text-xl text-primary" />}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="drawer-footer">
                <Toast ref={toast} />
                <Button label="Back" outlined severity="secondary" className="btn-back" onClick={onBack} />
                <Button
                    label="Place Order"
                    icon="pi pi-check-circle"
                    iconPos="right"
                    disabled={!selectedId || isLoading}
                    loading={isLoading}
                    className="btn-continue checkout-btn"
                    onClick={handleContinue}
                />
            </div>

            <Suspense fallback={<AppLoading />}>
                {isConfirmDialog && (
                    <PaymentConfirmDialog
                        showConfirmDialog={isConfirmDialog}
                        hideConfirmDialog={() => {
                            setIsConfirmDialog(false);
                            ProductStorageHelpers.SetProductStorage([]);
                            ProductStorageHelpers.ClearAllLocalStorage();
                            window.dispatchEvent(new Event('cartUpdated'));
                            dispatch(itemCount(0));
                            (onOrderComplete ?? onClose)();
                        }}
                        orderId={orderId}
                        PlutusTransactionReferenceId={plutusRefId}
                        orderDetails={finalOrderData}
                        paymentModeType={paymentModes.find(m => m.id === selectedId)?.mode}
                        amount={finalOrderData?.grandItemTotal ?? finalOrderData?.grandTotal ?? 0}
                    />
                )}

                {showMachineDialog && (
                    <UserSwipeMachineDialog
                        visible={showMachineDialog}
                        preSelectedUserId={JSON.parse(localStorage.getItem("user") || "{}").userID}
                        onHide={() => setShowMachineDialog(false)}
                        onSaved={() => {
                            setShowMachineDialog(false);
                        }}
                    />
                )}
            </Suspense>

            <Dialog 
                visible={showSuccess} 
                onHide={() => { 
                    ProductStorageHelpers.SetProductStorage([]);
                    ProductStorageHelpers.ClearAllLocalStorage();
                    window.dispatchEvent(new Event('cartUpdated'));
                    dispatch(itemCount(0));
                    (onOrderComplete ?? onClose)(); 
                    navigate(RouteConstant.products); 
                }} 
                header="Order Successfully Placed" 
                modal 
                style={{ width: '400px' }}
                closable={false}
            >
                <div className="flex flex-column align-items-center text-center p-2">
                    <i className="pi pi-check-circle text-6xl text-green-500 mb-4" />
                    <div className="text-xl font-bold mb-2">Thank you for your order!</div>
                    <p className="text-600 mb-4">Your order has been placed successfully.</p>

                    <div className="w-full surface-100 p-3 border-round mb-4 text-left">
                        <div className="flex justify-content-between mb-2 align-items-center">
                            <span className="text-600">Order ID:</span>
                            <span className="order-id-badge">
                                {orderId}
                            </span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Date:</span>
                            <span className="font-bold">{successDetails.date}</span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Total Amount:</span>
                            <span className="font-bold text-green-600">{Helpers.formatAmountInINR(successDetails.amount)}</span>
                        </div>
                        <div className="flex justify-content-between">
                            <span className="text-600">Est. Delivery:</span>
                            <span className="font-bold">{Helpers.formatDateWithShortMonthSpaced(successDetails.deliveryDate)}</span>
                        </div>
                    </div>

                    <div className="w-full flex flex-column gap-2">
                        <Button label="Back to Home" className="w-full checkout-btn" onClick={() => { 
                            ProductStorageHelpers.SetProductStorage([]);
                            ProductStorageHelpers.ClearAllLocalStorage();
                            window.dispatchEvent(new Event('cartUpdated'));
                            dispatch(itemCount(0));
                            (onOrderComplete ?? onClose)(); 
                            navigate(RouteConstant.products); 
                        }} />
                        <Button
                            label="View Order Details"
                            icon="pi pi-file-edit"
                            className="w-full checkout-btn"
                            loading={isLoading}
                            onClick={() => handleViewOrderDetail(orderId)}
                        />
                    </div>
                </div>
            </Dialog>

            {detailsOrder && (
                <Dialog
                    header="Order Details"
                    visible={visibleOrderDetail}
                    style={{ width: "90vw", maxWidth: "800px" }}
                    onHide={() => setVisibleOrderDetail(false)}
                    modal
                >
                    <TabView>
                        <TabPanel header="Order Details" leftIcon="pi pi-file mr-2">
                            <NewOrderReview 
                                TriggerPrint={() => {}} 
                                PrintEnabled={false} 
                                OrderData={<OrderPrint orderinfo={detailsOrder}/>} 
                            />
                        </TabPanel>
                        <TabPanel header="Order Print" leftIcon="pi pi-print mr-2">
                             <OrderPrint orderinfo={detailsOrder}/>
                        </TabPanel>
                    </TabView>
                </Dialog>
            )}
        </div>
    );
};

export default SidecartPayment;
